import React from 'react';
import styled from 'styled-components';
import { Body5, Container } from '@ui';
import { SkeletonButton, TransparentFlatButton } from '@ui/buttons';
import ErrorText from '@ui/ErrorText';
import { DOCUMENT_DOWNLOAD_KINDS, isPdfDocument } from '@utils/documentVault';
import { DOWNLOAD_STATUS, useDocumentDownload } from '@hooks/features/useDocumentDownload';

const ActionWrapper = styled.div`
  position: relative;

  button {
    text-align: left;
  }
`;

const DisabledActionWrapper = styled.div`
  opacity: 0.5;
  cursor: not-allowed;
`;

export const CLEAN_COPY_UNAVAILABLE_MESSAGE = 'Clean copy available for PDFs only.';

function formatElapsed(seconds) {
  const minutes = Math.floor(seconds / 60);
  const remainder = String(seconds % 60).padStart(2, '0');
  return `${minutes}:${remainder}`;
}

const CleanCopyUnavailable = () => (
  <DisabledActionWrapper title={CLEAN_COPY_UNAVAILABLE_MESSAGE}>
    <TransparentFlatButton fullWidth disabled>
      Download clean copy
    </TransparentFlatButton>
    <Container top={4}>
      <Body5 isLowContrast>{CLEAN_COPY_UNAVAILABLE_MESSAGE}</Body5>
    </Container>
  </DisabledActionWrapper>
);

export const DocumentDownloadActions = ({ doc, onActionComplete }) => {
  const {
    status,
    kind,
    error,
    statusCode,
    wasPassthrough,
    isRunning,
    showElapsedHint,
    elapsedSeconds,
    downloadOriginal,
    downloadCleanCopy,
  } = useDocumentDownload(doc);

  // `disabled` on the shared buttons only styles them — BaseButton still attaches onClick — so
  // the real single-flight guard lives in useDocumentDownload, not here.
  const canDownloadCleanCopy = isPdfDocument(doc);
  const isCleanCopyFailure = status === DOWNLOAD_STATUS.ERROR && kind === DOCUMENT_DOWNLOAD_KINDS.clean;

  const handleOriginalClick = async () => {
    const result = await downloadOriginal();
    if (result?.ok) onActionComplete?.();
  };

  const handleCleanCopyClick = async () => {
    const result = await downloadCleanCopy();
    if (result?.ok) onActionComplete?.();
  };

  return (
    <ActionWrapper>
      <TransparentFlatButton fullWidth disabled={isRunning} onClick={handleOriginalClick}>
        {isRunning && kind === DOCUMENT_DOWNLOAD_KINDS.original ? 'Downloading…' : 'Download original'}
      </TransparentFlatButton>

      <Container top={8}>
        {canDownloadCleanCopy ? (
          <TransparentFlatButton fullWidth disabled={isRunning} onClick={handleCleanCopyClick}>
            {isRunning && kind === DOCUMENT_DOWNLOAD_KINDS.clean ? 'Preparing clean copy…' : 'Download clean copy'}
          </TransparentFlatButton>
        ) : (
          <CleanCopyUnavailable />
        )}
      </Container>

      {showElapsedHint && kind === DOCUMENT_DOWNLOAD_KINDS.clean ? (
        <Container top={8}>
          <Body5 isLowContrast>
            Preparing clean copy… this can take up to 3 minutes. ({formatElapsed(elapsedSeconds)})
          </Body5>
        </Container>
      ) : null}

      {status === DOWNLOAD_STATUS.ERROR ? (
        <Container top={8}>
          <ErrorText>{error}</ErrorText>
          {isCleanCopyFailure ? (
            <Container top={4}>
              {/*
                Retry re-calls the clean endpoint only. The watermarked original stays a separate,
                explicit choice: falling back to it automatically would hand back the very
                watermark the user asked to remove.
              */}
              <SkeletonButton onClick={handleCleanCopyClick}>Try again</SkeletonButton>
              {statusCode === 502 ? (
                <Container top={4}>
                  <SkeletonButton onClick={handleOriginalClick}>Download watermarked original instead</SkeletonButton>
                </Container>
              ) : null}
            </Container>
          ) : null}
        </Container>
      ) : null}

      {status === DOWNLOAD_STATUS.COMPLETED && wasPassthrough ? (
        <Container top={8}>
          <Body5 isLowContrast>This file has no watermark to remove — downloaded the original.</Body5>
        </Container>
      ) : null}
    </ActionWrapper>
  );
};
